import ChoicesFactory from '../choices-factory';
import l10n from '../../lang/locallang';
import Button from '../button';
import Form from './form';
import FormValidator from '../validators/form-validator';
import MaskFactory from '../validators/components/mask';
import DisableCopyPasteFactory from '../disable-copy-paste';
import ChildDatelValidatorFactory from '../validators/components/child-date';
import ChildIndenticalValidatorFactory from '../validators/components/children-indentical-validation';
import CopyForm from './copy-form';
import Passport from '../pass-sprot';
import PassportDirectory from '../passport-directory';
import * as helpers from '../../helpers';

export default class FormFactory {
    static async create() {
        const formArray = [...document.querySelectorAll('form')];
        const formInstancesArray = [];

        await PassportDirectory.create();
        const passportInstance = new Passport();

        formArray.forEach((form) => {
            const submitButtonEl = form.querySelector('button[type="submit"]');
            const isFileInputExist = !!form.querySelector('input[type="file"]');

            // Create submit button instance
            const submitButtonWithoutTextChange = submitButtonEl ? new Button(submitButtonEl) : null;
            const submitButton = submitButtonEl ? new Button(submitButtonEl, {
                disableText: l10n.pleaseWait2,
                insertAfterText: isFileInputExist ? l10n.pleaseWaitForFile : '',
            }) : null;
            const isNotChangeTextOnSubmit = helpers.hasClass(form, '-is-not-change-submit-text');
            
            // Init masks
            let masksInstances = MaskFactory.create(form, '[data-mask]');

            const updateMasks = () => {
                masksInstances.forEach(mask => mask.destroy());
                masksInstances = MaskFactory.create(form, '[data-mask]');
            };

            // Init disable copy paste
            let disableCopyPasteInstances = DisableCopyPasteFactory.create('.js-disable-copy-paste');

            const updateDisableCopyPaste = () => {
                disableCopyPasteInstances.forEach(instance => instance.destroy());
                disableCopyPasteInstances = DisableCopyPasteFactory.create('.js-disable-copy-paste');
            };

            // Choices
            let choicesInstances = [];

            const destroyChoices = () => {
                choicesInstances.forEach((choicesInstance) => {
                    const { element } = choicesInstance.passedElement;
                    element.setAttribute('data-choice-value', element.value);
                    choicesInstance.destroy();
                });

                choicesInstances = [];
            };

            const initChoices = () => {
                choicesInstances = ChoicesFactory.create(form, '.js-choices');
            };

            setTimeout(() => {
                initChoices();
            }, 200);

            // Form validators
            const getValidators = () => {
                return [
                    ...ChildDatelValidatorFactory.create(form, '.js-child-date', submitButtonWithoutTextChange),
                    ...ChildIndenticalValidatorFactory.create(form, '.js-child-validation', submitButtonWithoutTextChange),
                ];
            };

            const formValidator = new FormValidator(form, getValidators());

            // Form instance
            const formInstance = new Form(
                form, 
                isNotChangeTextOnSubmit ? submitButtonWithoutTextChange : submitButton, 
                formValidator,
            );

            const update = () => {
                updateMasks();
                updateDisableCopyPaste();
                destroyChoices();
                initChoices();
                formInstance.update();
                formValidator.update(getValidators());
                passportInstance.update();
            };

            // Copy form elements logic
            const formCopyElements = [...form.querySelectorAll('.js-copy')];
            const formCopyButtons = formCopyElements.map((copyElement) => {
                const copyButtonEl = document.querySelector(`#${copyElement.getAttribute('data-copy-button-id')}`);
                return new Button(copyButtonEl);
            });

            const mainCopyInstances = [];
            let disciplineCopyInstances = [];

            /**
             * Inpit copy form for disciplines inside another copy form
             */
            // TODO: Create copy-form factory
            const createFormDisciplineCopy = () => {
                const formDisciplineElements = [...form.querySelectorAll('.js-copy-discipline')];
                const copyButtons = [...formCopyButtons];

                let maxDisciplineElementsCount = form.querySelector('[data-max-discipline-elements]');

                if (!maxDisciplineElementsCount) {
                    return;
                }

                maxDisciplineElementsCount = maxDisciplineElementsCount.getAttribute('data-max-discipline-elements');

                let currentDisciplineCount = formDisciplineElements.reduce((prev, current) => {
                    return prev + [...current.querySelectorAll('.js-block-to-copy-discipline')].length;
                }, 0);

                formDisciplineElements.forEach((disciplineCopyElement) => {
                    const copyButtonID = disciplineCopyElement.getAttribute('data-copy-button-id');
                    const copyButtonEl = document.querySelector(`#${copyButtonID}`);
                    const copyButton = new Button(copyButtonEl);
                    copyButtons.push(copyButton);

                    const manageCopyButtonsEnabled = () => {
                        if (currentDisciplineCount === +maxDisciplineElementsCount) {
                            copyButtons.forEach(copyButtonInstance => copyButtonInstance.disable());
                        } else {
                            copyButtons.forEach((copyButtonInstance) => {
                                if (!helpers.hasClass(copyButtonInstance.buttonEl, 'disabled-by-main-copy')) {
                                    copyButtonInstance.enable();
                                }
                            });
                            
                            mainCopyInstances.forEach(copyInstance => copyInstance.manageCopyButtonsEnabled());
                        }
                    };

                    manageCopyButtonsEnabled();
    
                    const disciplineCopy = new CopyForm(disciplineCopyElement, copyButton, form, {
                        afterChange: update,
                        beforeCopy: () => {
                            destroyChoices();
                        },
                        afterCopy: () => {
                            currentDisciplineCount += 1;
                            manageCopyButtonsEnabled();
                        },
                        afterDelete: () => {
                            currentDisciplineCount -= 1;
                            manageCopyButtonsEnabled();
                        },
                        onHTMLCopy: (htmlString) => {
                            return helpers.clearHTMLForCopy(htmlString, true);
                        },
                        submitLogic: false,
                        classList: {
                            copiedBlockModifier: '-is-copy-discipline',
                            removeButton: 'js-remove-button-discipline',
                            blockToCopy: 'js-block-to-copy-discipline',
                            index: 'js-index-discipline',
                        },
                    });

                    disciplineCopyInstances.push(disciplineCopy);
                });
            };

            const destroyFormDisciplineCopy = () => {
                disciplineCopyInstances.forEach(discipline => discipline.destroy());
                disciplineCopyInstances = [];
            };

            createFormDisciplineCopy();

            /**
             * Inpit main copy form
             */
            formCopyElements.forEach((copyElement, index) => {
                const mainCopyInstance = new CopyForm(copyElement, formCopyButtons[index], form, {
                    beforeCopy: () => {
                        destroyChoices();
                    },
                    afterChange: () => {
                        update();
                        destroyFormDisciplineCopy();
                        createFormDisciplineCopy();
                    },
                    onHTMLCopy: (htmlString) => {
                        return helpers.clearHTMLForCopy(htmlString, false);
                    },
                });

                mainCopyInstances.push(mainCopyInstance);
            });

            formInstancesArray.push(formInstance);
        });

        return formInstancesArray;
    }
}
